import { Inject, Injectable } from '@angular/core';
import { downgradeToAJSService, CommonsSessionStorageService } from 'ib-web-commons/utils';
import { GiftCardsAppConstant } from '@src/ib-gift-cards-constants';
import { TranslateService } from '@ngx-translate/core';
import { SummaryResponseServiceModel, SummaryDataServiceModel } from '@core/models/summary-service.model';
import { lastValueFrom } from 'rxjs';
import { SummaryProps } from 'commercial-management--ancillaries--web-components-design--library';
import { GeneralItem, GenericSummaryItem } from 'commercial-management--ancillaries--web-components-design--library/dist/components/layout/summary/Summary.model';

@Injectable({ providedIn: 'root' })

export class GiftCardsSummaryService {
    private currency: CurrencyResponse;
    private dataProps: SummaryProps;
    private configurationInfo: Types.ConfigurationModel;

    constructor (
        private commonsSessionStorageService: CommonsSessionStorageService,
        private translateService: TranslateService,
        @Inject('CurrencyService') private currencyService: any,
        @Inject('CommonsGenericProxy') private CommonsGenericProxy
    ) {}

    public getSummary = async (): Promise<SummaryProps> => {
        this.configurationInfo = this.commonsSessionStorageService.get<Types.ConfigurationModel>(GiftCardsAppConstant.SESSION_STORAGE.CONFIGURATION_INFO);
        this.currency = this.configurationInfo.importInfo.currency;
        const flowId: string = this.commonsSessionStorageService.get('flowId');
        const endpoint: string = GiftCardsAppConstant.DEFAULT_ENDPOINTS.SUMMARY.replace('{flowId}', flowId);
        const response: SummaryResponseServiceModel = await this.CommonsGenericProxy.getGeneric(endpoint);
        this.dataProps = await this.buildSummaryProps(response.data);
        return this.dataProps;
    };

    private buildSummaryProps = async (data: SummaryDataServiceModel): Promise<SummaryProps> => {
        this.currency.currencySymbol = data.currency.symbol;
        const formattedAmount = this.currencyService.formatPrice(this.currency, data.voucher.price.amountAccountable, true);
        const totalGiftcardAmount= this.currencyService.formatPrice(this.currency, data.voucher.price.amount, true);

        const summaryProps: SummaryProps = {
            otherItems: await this.buildGiftCardItemSummary(data, totalGiftcardAmount),
            title: await lastValueFrom(this.translateService.get('PAYMENTS_SUMMARY_TITLE')),
            totalPrice: formattedAmount,
            total: data.voucher.price.amountAccountable,
            totalGiftcardAmount: totalGiftcardAmount,
            expiryDateTo: data.voucher.campaign.expiryDateTo,
            upgradingBid: false,
            stopover: false
        };

        return summaryProps;
    };

    private async buildGiftCardItemSummary (data: SummaryDataServiceModel, totalGiftcardAmount: string): Promise<GenericSummaryItem[]> {
        const items: GenericSummaryItem[] = [];

        const giftCardItemSummary: GenericSummaryItem = {
            type: data.giftCardInfo.type !== GiftCardsAppConstant.SUBTYPES.GROUP ? 'GIFT_CARD' : 'GIFT_CARD_GROUP',
            total: totalGiftcardAmount,
            items: await this.buildGiftCardGeneralItems(data, totalGiftcardAmount)
        };

        items.push(giftCardItemSummary);
        const giftCardDiscountItem: GenericSummaryItem = await this.getGiftCardDiscount(data);
        if(giftCardDiscountItem) { items.push(giftCardDiscountItem); }

        return items;
    }

    private async buildGiftCardGeneralItems (data: SummaryDataServiceModel, totalGiftcardAmount: string): Promise<GeneralItem[]> {
        const items: GeneralItem[] = [];

        const giftCardItem: GeneralItem = {
            code: 'GC',
            description: await this.getGiftCardTypeDescription(data),
            price: totalGiftcardAmount
        };

        items.push(giftCardItem);

        return items;
    }

    private async getGiftCardTypeDescription (data: SummaryDataServiceModel): Promise<string> {
        return data.giftCardInfo.type === GiftCardsAppConstant.SUBTYPES.GROUP ?
            lastValueFrom(this.translateService.get('PMT_GIFT_CARD_GROUP_SUMMARY_TEXT')).then(text => text.replace('{{giftCardName}}', this.configurationInfo.giftCardName))
            : await lastValueFrom(this.translateService.get('PMT_GIFT_CARD_SUMMARY_TEXT'));
    }

    private async getGiftCardDiscount (data: SummaryDataServiceModel): Promise<GenericSummaryItem> {
        if (!Boolean(data.voucher.price.amountMarketing)) { return null; }
        const DISCOUNTS = await lastValueFrom(this.translateService.get('DISCOUNTS'));
        const discountAmount = data.voucher.price.amountAccountable - data.voucher.price.amount;
        const formattedDiscountAmount = this.currencyService.formatPrice(this.currency, discountAmount, true);
        const discountItem: GenericSummaryItem = {
            type: DISCOUNTS,
            total: formattedDiscountAmount,
            items: await this.getGiftCardDiscountItems(formattedDiscountAmount, data)
        };
        return discountItem;
    }

    private async getGiftCardDiscountItems (formattedDiscountAmount: string, data: SummaryDataServiceModel): Promise<GeneralItem[]> {
        const PMT_GIFT_CARD_SUMMARY_PERCENTAGE_DISCOUNT = await lastValueFrom(this.translateService.get('PMT_GIFT_CARD_SUMMARY_PERCENTAGE_DISCOUNT'));
        const formattedDiscountItemText = PMT_GIFT_CARD_SUMMARY_PERCENTAGE_DISCOUNT.replace('{{percentage}}', data.voucher.price.percentage);

        const discountItem: GeneralItem = {
            code: 'VOUCHER',
            price: formattedDiscountAmount,
            description: formattedDiscountItemText
        };

        return [discountItem];
    }
}

downgradeToAJSService('GiftCardsSummaryService', GiftCardsSummaryService, 'ib-gift-cards-payments-module');
